
import React, {useEffect, useRef, useState} from "react";
import { Box, Step, StepLabel, Stepper, Tab, Tabs } from "@mui/material";
import ConnectorWizard from "./ConnectorWizard";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

/**
 * StepperTabs Component
 *
 * This component provides a tab-based or stepper-based navigation UI.
 * It supports swipeable views and optional steps.
 *
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @JobTitle: Full Stack Developer
 *
 * @param {Object} props - Component properties.
 * @param {React.ReactNode} props.children - Content to be displayed in each step/tab.
 * @param {string} [props.id] - Unique identifier for the stepper.
 * @param {string[]} props.steps - Array of step/tab labels.
 * @param {number} props.activeStep - Index of the currently active step.
 * @param {Function} props.isStepOptional - Function that determines if a step is optional.
 * @param {Function} props.setActiveStep - Callback to set the active step.
 * @param {Function} props.handleStepChange - Function to handle tab/step changes.
 * @param {boolean} [props.onlyTab=false] - If true, renders only the tab view.
 * @param {boolean} [props.disabled=false] - If true, disables stepper navigation.
 *
 * @returns {JSX.Element}
 */
const StepperTabs = (props) => {
    const { children, id, steps, activeStep, isStepOptional, setActiveStep, handleStepChange, onlyTab=false, disabled=false} = props;
    const { t } = useTranslation();
    const [animClass, setAnimClass] = useState('');
    const prevSelected = useRef(0);
    const touchStartX = useRef(null);

    // The position of the active step is resolved while rendering: keeping it in a state
    // updated by an effect left a frame with the previous tab still marked as selected,
    // where a second fast click reached MUI as a new selection and re-ran the step change.
    const activePosition = steps?.findIndex(s => s?.index === activeStep);
    const selected = activePosition >= 0 ? activePosition : (activeStep ?? 0);

    useEffect(() => {
        if (selected !== prevSelected.current) {
            setAnimClass(selected > prevSelected.current ? 'slide-in-right' : 'slide-in-left');
            prevSelected.current = selected;
        }
    }, [selected]);

    /** Records the horizontal touch start position for swipe detection. */
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };

    /**
     * Evaluates the horizontal swipe distance on touch end.
     * Requires >50px displacement to trigger a step change,
     * preventing accidental navigation on small or vertical gestures.
     */
    const handleTouchEnd = (e) => {
        if (touchStartX.current === null) return;
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            const nextPos = diff > 0 ? selected + 1 : selected - 1;
            if (nextPos >= 0 && nextPos < steps.length) {
                const nextStep = steps[nextPos];
                handleStepChange(nextStep?.index ?? nextPos);
            }
        }
        touchStartX.current = null;
    };

    return <>
        {((id && true) || onlyTab || disabled) ?
            <>
                { steps?.length > 0 &&
                    <Box className="tab-main-mui mb-3" >
                        <Tabs
                            className={"w-100 minw-100 maxw-100"}
                            value={selected}
                            onChange={(event, newValue) => {
                                let value = event?.target?.id ? parseInt(event.target.id.replace("tab_st_index_", "")): newValue;
                                // Clicking the tab already displayed must not run the step change again:
                                // it resets the search of the module and empties the list that is shown.
                                if (value === activeStep) return;
                                handleStepChange(value);
                            }}
                            variant="scrollable"
                            scrollButtons={"auto"}
                            aria-label="scrollable prevent tabs example"
                        >
                            {
                                steps.map((e, i) => {
                                    let index = e?.index || i;
                                    let label = e?.label || e;
                                    return (
                                        <Tab
                                            className={`tap_hover steps-global min-w-auto ${activeStep === index ? "active_tap btn-bordered-global" : "deactive_tap"}`}
                                            id={`tab_st_index_${index}`}
                                            label={label}
                                            key={index}
                                            style={{ "--steps-count": steps.length}}
                                        />
                                    );
                                })
                            }

                        </Tabs>
                    </Box>
                }
            </>
            :
            <div className={"d-flex justify-content-center"}>
                <div className={"custom-step-min-width"}>
                    <Stepper className="stepper-font mt-3 " activeStep={activeStep} alternativeLabel connector={<ConnectorWizard />}>
                        {steps.map((e, i) => {
                            let index = e?.index || i;
                            let label = e?.label || e;

                            const labelProps = {};
                            if (isStepOptional && isStepOptional(index)) {
                                labelProps.optional = (
                                    <Typography variant="caption" className={(activeStep === index ? "active": index < activeStep ? 'completed': 'disabled')}>{t('(optional)')}</Typography>
                                );
                            }
                            const disabled = index > activeStep;

                            return (
                                <Step id={`step-st-index-${index}`} key={index}>
                                    <StepLabel id={`step-label-st-index-${index}`} className="cursor-pointer text-center stepper-font text-capitalize" {...labelProps}
                                               onClick={() => {
                                                   if (!disabled) {
                                                       setActiveStep(index)
                                                   }
                                               }}
                                    >{label}
                                    </StepLabel>
                                </Step>
                            )
                        })}
                    </Stepper>
                </div>
            </div>
        }

        <div
            className={`stepper-swipe-content ${animClass}`}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onAnimationEnd={() => setAnimClass('')}
        >
            {React.Children.toArray(children)[activeStep] ?? null}
        </div>

    </>
}

export default StepperTabs;
